import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'
import PropTypes from 'prop-types'
import CurrencyAmountCell from '../cell-renderer/currency-amount-cell'
import clsx from 'clsx'

const { CENTER, RIGHT } = {
  CENTER: 'center',
  RIGHT: 'right',
}

const useStyles = makeStyles((theme) => ({
  columnContainer: {
    cursor: 'pointer',
    width: '100%',
  },
  topCell: {
    display: 'flex',
  },
  headerCellLabel: {
    '&:hover $grabIcon': {
      opacity: 1,
    },
  },
  labelCenter: {
    justifyContent: 'center',
  },
  labelRight: {
    justifyContent: 'flex-end',
  },
  subtitle: {
    color: theme.darkGrey,
    fontSize: 12,
    fontWeight: 'normal',
    marginTop: 4,
  },
  truncate: theme.mixins.truncateText,
  grabIcon: {
    cursor: 'pointer',
    position: 'absolute',
    left: 9,
    top: 21,
    opacity: 0,
    transition: 'opacity 0.1s ease-in-out',
  },
  menuVisible: {
    '&.ag-header-icon.ag-header-cell-menu-button': {
      opacity: '1',
      '& .listIcon.icon-filter': {
        fontSize: 13,
        color: theme.primary,
      },
    },
  },
  menuHidden: {
    opacity: 'inherited',
    '& .listIcon.icon-filter': {
      fontSize: 13,
      color: theme.primary,
    },
  },
  sumSection: {
    display: 'flex',
    marginLeft: 'auto',
    marginTop: 6,
    alignItems: 'center',
  },
  sumIcon: {
    marginRight: 4.6,
    fontSize: 10,
  },
  sumText: {
    fontSize: 12,
  },
}))

const NON_SORTING_TARGETS = '.ag-header-cell-menu-button, .ag-header-cell-resize'

const ListColumnHeader = (props) => {
  const [ascSort, setAscSort] = useState(false)
  const [noSort, setNoSort] = useState(true)
  const refButton = useRef(null)
  const refContainer = useRef(null)
  const {
    api,
    showColumnMenu,
    setSort,
    column,
    enableMenu,
    enableSorting,
    displayName,
    column: { colDef: { sortable = false, listSettingName, name } = {} } = { },
  } = props
  const icons = api?.gridOptionsService?.gridOptions?.icons || {}
  const classes = useStyles()
  const isServerSideRowModel = api.rowModel.constructor.name === 'ServerSideRowModel'
  const listState = useSelector(state => state.lists)
  const [headerSum, setHeaderSum] = useState(0)

  useEffect(() => {
    if (isServerSideRowModel && !!listSettingName) {
      const metaData = listState[listSettingName].meta || {}
      setHeaderSum(metaData[name])
    } else {
      setHeaderSum(api.getModel().rowsToDisplay?.reduce((headerSum, row) => {
        if (row.data && Object.prototype.hasOwnProperty.call(row.data, column.colId) && row.data[column.colId]) {
          return headerSum + parseFloat(row.data[column.colId])
        }
        return headerSum
      }, 0))
    }
  }, [api.getModel().rowsToDisplay, listState[listSettingName]?.meta])

  const onMenuClicked = () => {
    showColumnMenu(refButton.current)
  }

  const onSortChanged = () => {
    setAscSort(!column.isSortAscending())
    setNoSort(!(column.isSortAscending() || column.isSortDescending()))
  }

  const onSortRequested = (order, event) => {
    if (sortable) {
      setSort(order, event.shiftKey)
    }
  }

  useEffect(() => {
    column.addEventListener('sortChanged', onSortChanged)
    onSortChanged()

    return () => {
      column.removeEventListener('sortChanged', onSortChanged)
    }
  }, [])

  useEffect(() => {
    const headerCell = refContainer.current?.closest('.ag-header-cell')
    if (!headerCell) return

    const onHeaderCellClicked = (event) => {
      if (event.target.closest(NON_SORTING_TARGETS)) return
      onSortRequested(ascSort ? 'asc' : 'desc', event)
    }

    headerCell.addEventListener('click', onHeaderCellClicked)

    return () => {
      headerCell.removeEventListener('click', onHeaderCellClicked)
    }
  }, [ascSort, sortable, setSort])

  let aggregation = null
  if (column.aggregationActive) {
    aggregation = (
      <div className={clsx('ag-header-cell-amount', classes.sumSection)}>
        <div className={clsx('icon icon-sum', classes.sumIcon)}></div>
        <CurrencyAmountCell value={headerSum} customClass={classes.sumText} />
      </div>
    )
  }

  let menu = null
  if (enableMenu && column.colDef.filter) {
    menu = (
      <div
        ref={refButton}
        className={clsx('ag-header-icon ag-header-cell-menu-button', column.filterActive ? classes.menuVisible : classes.menuHidden)}
        dangerouslySetInnerHTML={{ __html: icons?.menu }}
        onClick={() => onMenuClicked()}
      />
    )
  }

  let sort = null
  if (enableSorting && !noSort) {
    const sortClassName = ascSort ? 'ag-sort-descending-icon' : 'ag-sort-ascending-icon'
    const sortIcon = ascSort ? icons?.sortDescending : icons?.sortAscending
    sort = (<div
      className={clsx('ag-header-icon ag-header-label-icon', sortClassName)}
      dangerouslySetInnerHTML={{ __html: sortIcon }} />)
  }

  let alignment = null
  switch (column.colDef?.alignment) {
    case CENTER:
      alignment = classes.labelCenter
      break
    case RIGHT:
      alignment = classes.labelRight
      break
  }

  return (
    <div className={classes.columnContainer} ref={refContainer}>
      <div className={classes.topCell}>
        <div className={clsx('ag-header-cell-label', classes.headerCellLabel, alignment)}>
          {!column.colDef?.suppressMovable && <span className={clsx('ag-cell-grab-icon icon icon-grab', classes.grabIcon)}></span>}
          <div
            className='ag-header-cell-text'
            title={column.colDef?.headerTooltip || undefined}
          >
            {displayName}
          </div>
          {sort}
        </div>
        {menu}
      </div>
      {aggregation}
      {(!!column.colDef?.subtitle && !aggregation) && <div className={clsx(classes.subtitle, classes.truncate)}>{column.colDef.subtitle}</div>}
    </div>
  )
}

ListColumnHeader.propTypes = {
  agGridReact: PropTypes.object,
  column: PropTypes.object.isRequired,
  showColumnMenu: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  enableMenu: PropTypes.bool.isRequired,
  enableSorting: PropTypes.bool.isRequired,
  displayName: PropTypes.string,
}

export default ListColumnHeader
